import React from 'react';

import { Box, Text } from '@betterment/samba-react';

import { EditNameForm } from './EditNameForm';

import { SambaAutoLink } from '../../../features/navigation/SambaAutoLink';
import { ModalSection } from '../../modal-section/ModalSection';
import { ModalSectionRow } from '../../modal-section/ModalSectionRow';
import { formatLegalAccountType } from '../helpers';

import type { AccountSettingsUrlBuilders, RenderCloseAccountAction } from '../contentTypes';
import type { DirectTradingAccount } from '../data';

type DirectTradingAccountSettingsProps = {
  account: DirectTradingAccount;
  urlBuilders: Pick<AccountSettingsUrlBuilders, 'accountDetails' | 'editPortfolio' | 'legalAccounts'>;
  renderCloseAccount?: RenderCloseAccountAction;
  handleNameUpdate?: (newName?: string) => Promise<void>;
};

export const DirectTradingAccountSettings: React.FC<DirectTradingAccountSettingsProps> = ({
  account,
  urlBuilders,
  renderCloseAccount,
  handleNameUpdate,
}) => {
  const { legalAccount } = account;
  const legalAccountType = formatLegalAccountType(legalAccount);
  const hasBeneficiaries = legalAccount.primaryBeneficiaries.length > 0;

  const closeAccountAction =
    renderCloseAccount &&
    renderCloseAccount({
      balance: account.balance,
      purposeName: account.envelope.purpose?.name,
      name: account.name,
      legacyAccountId: account.legacySubAccountId,
    });

  return (
    <Box css={{ display: 'grid', gap: 'space.200' }}>
      <ModalSection header="Account details" cta={urlBuilders.accountDetails}>
        <EditNameForm
          name={account.nameOverride}
          accountDescription={account.accountDescription}
          handleNameUpdate={handleNameUpdate}
        />
        <ModalSectionRow>
          <ModalSectionRow.LeadingContent>
            <Text variant="text50" color="text.weak">
              Account type
            </Text>
            <Text>{legalAccountType}</Text>
          </ModalSectionRow.LeadingContent>
        </ModalSectionRow>
        {!hasBeneficiaries && urlBuilders.legalAccounts && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text variant="text50" color="text.weak">
                Beneficiaries
              </Text>
              <Text>None</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <SambaAutoLink to={urlBuilders.legalAccounts}>Add</SambaAutoLink>
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
      </ModalSection>
      {closeAccountAction}
    </Box>
  );
};
